

import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import PropTypes from 'prop-types';
import CssBaseline from '@mui/material/CssBaseline';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import Tooltip from '@mui/material/Tooltip';
import Alert from './Alert';
import NavDrawer from './NavDrawer';

import useScrollTrigger from '@mui/material/useScrollTrigger';
import Slide from '@mui/material/Slide';
import { styled, alpha, useTheme } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import ColorModeContext from '../contexts/ColorMode';

const drawerWidth = 240;

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  alignItems: 'flex-start',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(2),
  backgroundColor: theme.palette.primary,
  // Override media queries injected by theme.mixins.toolbar
  '@media all': {
    minHeight: 100,
  },
}));

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

function HideOnScroll(props) {
  const { children, window } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

HideOnScroll.propTypes = {
  children: PropTypes.element.isRequired,
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window: PropTypes.func,
};


function ResponsiveAppbar(props) {
  const theme = useTheme();
  const colorMode = React.useContext(ColorModeContext);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [openThemeAlert, setOpenThemeAlert] = React.useState(false);

  const handleDrawerToggle = () => {
    setOpenDrawer(!openDrawer);
  };

  return (
    <>
      {/* large breakpoint support */}
      <Box sx={{ display: { lg: 'flex', xs: 'none' } }}>
        <CssBaseline />
        <AppBar
          position="static"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
          }}
        >
          <Toolbar sx={{ backgroundColor: theme.palette.primary }}>
            <Typography align="left" sx={{ flexGrow: 1, }} variant="h6" noWrap component="div">
              Bryan Maldonado
          </Typography>

            <Tooltip title={theme.palette.mode === 'dark' ? "Light Theme" : "Dark Theme"} arrow>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={colorMode.toggleColorMode}
                sx={{ display: { sm: 'flex' } }}
              >
                {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>


          </Toolbar>
        </AppBar>
        <NavDrawer open={openDrawer} handleDrawerToggle={() => true} />
      </Box>
      {/* Suppor medium breakpoint half screen and table */}
      <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex', lg: 'none' } }}>
        <AppBar position="static">
          <StyledToolbar>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="open drawer"
              sx={{ mr: 6 }}
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h5"
              noWrap
              component="div"
              sx={{ flexGrow: 1, fontWeight: 700, alignSelf: 'flex-end', }}
              align="center"
            >
              Bryan Maldonado
          </Typography>
            <IconButton size="large" aria-label="search" color="inherit">
              <SearchIcon />
            </IconButton>
            <IconButton
              size="large"
              aria-label="display more actions"
              edge="end"
              color="inherit"
              onClick={colorMode.toggleColorMode}
            >
              {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </StyledToolbar>
        </AppBar>
        <NavDrawer open={openDrawer} handleDrawerToggle={handleDrawerToggle} />
      </Box>

      <Alert open={openThemeAlert} onClose={() => setOpenThemeAlert(false)} message="Theme changed!" type="success" />


      {/* mobile app bar */}
      <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
        <HideOnScroll {...props}>
          <AppBar sx={{ display: { xs: 'flex', lg: 'none', } }}>
            <Toolbar sx={{ backgroundColor: theme.palette.primary }}>
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="open drawer"
                sx={{ display: { xs: 'none', sm: 'flex' } }}
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{ flexGrow: 1, textAlign: 'left', display: { xs: 'flex', sm: 'block' } }}
              >
                Bryan Maldonado
          </Typography>
              <Search sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="Search…"
                  inputProps={{ 'aria-label': 'search' }}
                />
              </Search>

              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="open drawer"
                sx={{ justifyContent: 'flex-end', display: { xs: 'flex', sm: 'none' } }}
              >
                <SearchIcon />
              </IconButton>

              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="open drawer"
                sx={{ justifyContent: 'flex-end', display: { xs: 'flex', sm: 'none' } }}
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
        </HideOnScroll>
        <Box sx={{ display: { xs: 'flex', md: 'none', }, marginBottom: 6, }}>
          {/* <Toolbar /> */}
          {/* <div style={{ marginBottom: 16, }} /> */}
        </Box>
        <NavDrawer open={openDrawer} handleDrawerToggle={handleDrawerToggle} />
      </Box>

    </>
  );
}

ResponsiveAppbar.propTypes = {
};

export default ResponsiveAppbar;
